import { useAuthenticated } from '@gettactic/hooks';
import { UserPropertyType } from '@gettactic/api';
import { WeeklyDataLayout } from '@gettactic/components/src/components/WeeklyOffice/WeeklyDataLayout';
import { format } from 'date-fns';
import { WeeklyTable } from './TeamSchedule/WeeklyTable';
import { ChartConfig } from '@gettactic/components/src/@v2/components/ui/chart';
import { InOfficeChartLegend } from '@gettactic/components/src/components/WeeklyOffice/legend/InOfficeChartLegend';
import { WeeklyChart } from './TeamSchedule/WeeklyChart';
import { TeamAttendanceHeader } from './TeamSchedule/TeamAttendanceHeader';
import { TeamAttendanceFilters } from './TeamSchedule/TeamAttendanceFilters';
import { useTeamAttendanceFilters } from './TeamSchedule/hooks/useTeamAttendanceFilters';
import { api } from '@/lib/api/api';
import { SelectUsers } from './SelectUsers';
import { useMemo } from 'react';
import { ReservationModals } from './reservation-modals/ReservationModals';

const chartConfig: ChartConfig = {
  inOffice: {
    label: 'In office',
    color: 'hsl(var(--chart-1))',
    indicator: 'dot'
  },
  favorite: {
    color: 'hsl(var(--chart-2))',
    label: 'Favorite',
    indicator: 'circle'
  }
};

const TeamSchedule = ({ daysCount }: { daysCount: number }) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const {
    filters: dashboardFilters,
    isComponentLoading,
    totalUsers,
    tableOverviewData,
    chartData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    setUsersFilter,
    setTeams,
    handleSaveFilterData
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice
  });

  const { formattedWeek, somethingWentWrong } = useMemo(() => {
    try {
      const formatted =
        dashboardFilters.week?.map((date) => format(date, 'yyyy-MM-dd')) || [];
      return { formattedWeek: formatted, somethingWentWrong: false };
    } catch (e) {
      return { formattedWeek: [], somethingWentWrong: true };
    }
  }, [dashboardFilters.week]);

  if (somethingWentWrong) {
    return (
      <div className="flex justify-center p-4">
        Unable to process date information
      </div>
    );
  }

  return (
    <div className="mt-4 mb-12 flex flex-row justify-center md:mt-16">
      <div className="flex w-full flex-col space-y-4">
        <div className="flex flex-col items-end justify-between md:flex-row">
          <TeamAttendanceHeader
            authenticatedUserName={authenticatedUser.user?.name}
            totalUsers={totalUsers}
          />
          <TeamAttendanceFilters
            teams={dashboardFilters.teams}
            onTeamsChange={setTeams}
            onSaveFilterData={handleSaveFilterData}
            isComponentLoading={isComponentLoading}
          />
        </div>
        <div>
          <WeeklyDataLayout
            chart={
              <WeeklyChart
                daysCount={daysCount}
                isLoading={isLoading}
                chartInfo={{
                  chartData: chartData,
                  chartConfig: chartConfig
                }}
                myId={authenticatedUser.user?.id || ''}
                userCount={totalUsers}
              />
            }
            legend={<InOfficeChartLegend chartConfig={chartConfig} />}
            table={
              <WeeklyTable
                daysCount={daysCount}
                userSearch={
                  <SelectUsers
                    className={'hidden md:block'}
                    // disabled={isComponentLoading}
                    onChange={(ids: string[]) => {
                      handleSaveFilterData(
                        UserPropertyType.LastUsersFilter,
                        ids
                      );
                      setUsersFilter(ids);
                    }}
                    values={dashboardFilters.users}
                  />
                }
                authenticatedUser={authenticatedUser}
                data={tableOverviewData}
                formattedWeek={formattedWeek}
                favsApi={api}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
              />
            }
            daysCount={daysCount}
          />
        </div>
      </div>
      <ReservationModals />
    </div>
  );
};

export default TeamSchedule;
